import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Statistic,
  Row,
  Col,
  Button,
  Space,
  Select,
  message,
  Modal,
  Empty,
  Tag,
} from 'antd';
import {
  EyeOutlined,
  TeamOutlined,
  FileTextOutlined,
  UserAddOutlined,
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { analyticsAPI } from '../services/api';

// ---------------------------------------------------------------------------
// Simple SVG Line Chart Component (no external dependencies)
// ---------------------------------------------------------------------------
const TrendChart = ({ data, height = 300 }) => {
  const [tooltip, setTooltip] = useState(null);

  const padding = { top: 24, right: 24, bottom: 48, left: 56 };
  const width = 800;
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const points = useMemo(() => {
    if (!data || data.length === 0) return [];
    const maxCount = Math.max(...data.map((d) => d.count), 1);
    return data.map((d, i) => ({
      x: padding.left + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2),
      y: padding.top + chartH - (d.count / maxCount) * chartH,
      date: d.date,
      count: d.count,
    }));
  }, [data, chartW, chartH, padding.left, padding.top]);

  if (!data || data.length === 0) {
    return <Empty description="No trend data" />;
  }

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const yTicks = 5;
  const yStep = maxCount / yTicks;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath =
    linePath +
    ` L${points[points.length - 1].x},${padding.top + chartH}` +
    ` L${points[0].x},${padding.top + chartH} Z`;

  const gradientId = 'area-gradient';

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', minHeight: height }}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1890ff" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1890ff" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Horizontal grid lines + Y axis labels */}
        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const val = Math.round(yStep * (yTicks - i));
          const y = padding.top + (i / yTicks) * chartH;
          return (
            <g key={`y-${i}`}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#f0f0f0"
                strokeWidth="1"
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill="#8c8c8c"
              >
                {val}
              </text>
            </g>
          );
        })}

        {/* X axis labels (every 5th) */}
        {points.map((p, i) => {
          if (i % 5 !== 0 && i !== points.length - 1) return null;
          const label = p.date.length > 5 ? p.date.slice(5) : p.date; // MM-DD
          return (
            <text
              key={`x-${i}`}
              x={p.x}
              y={padding.top + chartH + 20}
              textAnchor="middle"
              fontSize="11"
              fill="#8c8c8c"
            >
              {label}
            </text>
          );
        })}

        {/* Area fill */}
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Line */}
        <path d={linePath} fill="none" stroke="#1890ff" strokeWidth="2.5" strokeLinejoin="round" />

        {/* Data points + invisible hover targets */}
        {points.map((p, i) => (
          <g key={`pt-${i}`}>
            <circle cx={p.x} cy={p.y} r="3" fill="#fff" stroke="#1890ff" strokeWidth="2" />
            <circle
              cx={p.x}
              cy={p.y}
              r="12"
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setTooltip({ x: p.x, y: p.y, date: p.date, count: p.count })}
              onMouseLeave={() => setTooltip(null)}
            />
          </g>
        ))}

        {/* Tooltip */}
        {tooltip && (
          <g>
            <line
              x1={tooltip.x}
              y1={padding.top}
              x2={tooltip.x}
              y2={padding.top + chartH}
              stroke="#1890ff"
              strokeWidth="1"
              strokeDasharray="4 3"
              opacity="0.5"
            />
            <rect
              x={tooltip.x - 60}
              y={tooltip.y - 46}
              width="120"
              height="38"
              rx="6"
              fill="rgba(0,0,0,0.75)"
            />
            <text x={tooltip.x} y={tooltip.y - 30} textAnchor="middle" fontSize="11" fill="#ffffffcc">
              {tooltip.date}
            </text>
            <text x={tooltip.x} y={tooltip.y - 15} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="600">
              {tooltip.count} 次造訪
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Analytics Page
// ---------------------------------------------------------------------------
const AnalyticsPage = () => {
  const [summary, setSummary] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [leads, setLeads] = useState([]);
  const [trendDays, setTrendDays] = useState(14);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Fetch summary stats
  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await analyticsAPI.getSummary();
      setSummary(res.data || res);
    } catch (err) {
      message.error('無法載入統計摘要');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  // Fetch visit trend
  const fetchTrend = useCallback(async (days) => {
    setLoadingTrend(true);
    try {
      const res = await analyticsAPI.getVisitTrend(days);
      setTrendData(res.data || res || []);
    } catch (err) {
      message.error('無法載入造訪趨勢');
    } finally {
      setLoadingTrend(false);
    }
  }, []);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const res = await analyticsAPI.getLeads();
      setLeads(res.data || res || []);
    } catch (err) {
      message.error('無法載入銷售線索');
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchLeads();
  }, [fetchSummary, fetchLeads]);

  useEffect(() => {
    fetchTrend(trendDays);
  }, [trendDays, fetchTrend]);

  // Delete lead
  const handleDeleteLead = (record) => {
    Modal.confirm({
      title: '確認刪除',
      icon: <ExclamationCircleOutlined />,
      content: `確定要刪除「${record.company_name || record.contact_person || record.email}」的線索資料？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await analyticsAPI.deleteLead(record.id);
          message.success('已刪除');
          setLeads((prev) => prev.filter((l) => l.id !== record.id));
        } catch {
          message.error('刪除失敗');
        }
      },
    });
  };

  // CSV export
  const handleExportCSV = () => {
    if (!leads || leads.length === 0) {
      message.warning('沒有可匯出的資料');
      return;
    }
    const headers = [
      '公司名稱',
      '聯絡人',
      'Email',
      '電話',
      '興趣/需求',
      '來源頁面',
      '投影片索引',
      '建立時間',
    ];
    const rows = leads.map((l) => [
      l.company_name || '',
      l.contact_person || '',
      l.email || '',
      l.phone || '',
      l.interest || '',
      l.source_page || '',
      l.slide_index ?? '',
      l.created_at || '',
    ]);

    const BOM = '\uFEFF';
    const csvContent =
      BOM +
      [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('CSV 匯出成功');
  };

  // Table columns
  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: '公司名稱',
      dataIndex: 'company_name',
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#bfbfbf' }}>--</span>,
    },
    {
      title: '聯絡人',
      dataIndex: 'contact_person',
      width: 110,
      render: (v) => v || <span style={{ color: '#bfbfbf' }}>--</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      ellipsis: true,
      render: (v) => (v ? <a href={`mailto:${v}`}>{v}</a> : '--'),
    },
    {
      title: '電話',
      dataIndex: 'phone',
      width: 130,
      render: (v) => v || '--',
    },
    {
      title: '興趣/需求',
      dataIndex: 'interest',
      width: 150,
      ellipsis: true,
      render: (v) =>
        v ? (
          <Tag color="blue" style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {v}
          </Tag>
        ) : (
          '--'
        ),
    },
    {
      title: '來源頁面',
      dataIndex: 'source_page',
      width: 130,
      ellipsis: true,
      render: (v, record) => (
        <span>
          {v || '--'}
          {record.slide_index != null && (
            <Tag color="geekblue" style={{ marginLeft: 4 }}>
              #{record.slide_index}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      width: 170,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
      render: (v) => {
        if (!v) return '--';
        const d = new Date(v);
        return d.toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => handleDeleteLead(record)}
        />
      ),
    },
  ];

  // Stat cards config
  const statCards = [
    {
      title: '總造訪數',
      value: summary?.total_visits,
      icon: <EyeOutlined />,
      color: '#1890ff',
    },
    {
      title: '今日造訪',
      value: summary?.today_visits,
      icon: <CalendarOutlined />,
      color: '#52c41a',
    },
    {
      title: '簡報瀏覽',
      value: summary?.total_slide_views,
      icon: <FileTextOutlined />,
      color: '#722ed1',
    },
    {
      title: '銷售線索',
      value: summary?.total_leads,
      icon: <UserAddOutlined />,
      color: '#fa8c16',
    },
    {
      title: '不重複訪客',
      value: summary?.unique_visitors,
      icon: <TeamOutlined />,
      color: '#eb2f96',
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
          訪客分析 &amp; 銷售線索
        </h2>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchSummary();
            fetchTrend(trendDays);
            fetchLeads();
          }}
        >
          重新整理
        </Button>
      </div>

      {/* Summary stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map((s, i) => (
          <Col xs={24} sm={12} md={8} lg={4} xl={4} key={i} flex={1}>
            <Card loading={loadingSummary} bordered={false} style={{ borderRadius: 8 }}>
              <Statistic
                title={
                  <span style={{ fontSize: 13 }}>
                    {React.cloneElement(s.icon, {
                      style: { color: s.color, marginRight: 6 },
                    })}
                    {s.title}
                  </span>
                }
                value={s.value ?? '--'}
                valueStyle={{ color: s.color, fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Trend chart */}
      <Card
        title="每日造訪趨勢"
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 8 }}
        loading={loadingTrend}
        extra={
          <Select
            value={trendDays}
            onChange={(v) => setTrendDays(v)}
            style={{ width: 100 }}
            options={[
              { label: '7 天', value: 7 },
              { label: '14 天', value: 14 },
              { label: '30 天', value: 30 },
            ]}
          />
        }
      >
        <TrendChart data={trendData} />
      </Card>

      {/* Leads table */}
      <Card
        title={`銷售線索 (${leads.length})`}
        bordered={false}
        style={{ borderRadius: 8 }}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={leads.length === 0}
            >
              匯出 CSV
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={leads}
          columns={columns}
          rowKey="id"
          loading={loadingLeads}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `共 ${total} 筆`,
          }}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ emptyText: <Empty description="尚無銷售線索" /> }}
        />
      </Card>
    </div>
  );
};

export default AnalyticsPage;
